import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  ViewChild,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import Quill from 'quill';
import { Editor } from 'primeng/editor';
import { REGEX } from '@common/constants/regex.constant';

@Component({
  selector: 'app-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextEditorComponent),
      multi: true,
    },
  ],
})
export class TextEditorComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  @ViewChild('editor') editor!: Editor;
  @Input() height: string = 'auto';
  @Input() placeholder: string = '';

  value: string = '';

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};
  @Input() charLimit: number ;
  @Input() otherSectionsCount: number = 0;
  charCount : number = 0;
  pendingLimitAlert: boolean = false;
  @Output() valueChange = new EventEmitter<{ value: string, charCount: number }>();

  ngOnInit(): void {
    // Register Custom Clipboard module early
    const Clipboard = Quill.import('modules/clipboard');

    class CustomClipboard extends Clipboard {
      convert(html: string) {
        // Call original convert to get delta
        const delta = super.convert(html);

        // Remove any background attributes from delta ops
        delta.ops.forEach((op: any) => {
          if (op.attributes?.background) {
            delete op.attributes.background;
          }
        });

        return delta;
      }
    }

    Quill.register('modules/clipboard', CustomClipboard, true);
  }

  ngAfterViewInit(): void {
    const quill = (this.editor as any)?.quillEditor;
    if (quill) {
      const editorElem = quill.root;
      editorElem.style.overflowY = 'hidden';

      const resizeEditor = () => {
        editorElem.style.height = 'auto';
        editorElem.style.height = `${editorElem.scrollHeight}px`;
      };

      resizeEditor();

      quill.on('text-change', () => {
        resizeEditor();
        const html = quill.root.innerHTML;
        if (html !== this.value) {
          this.value = html;
          this.onChange(this.value);
          this.onTouched();
        }
      });
    }
  }
  getWordsWithIndices(text: string): { word: string; start: number; end: number }[] {
    const regex = REGEX.WORD_WITH_APOSTROPHE_AND_HYPHEN;
    const result = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      result.push({ word: match[0], start: match.index, end: match.index + match[0].length });
    }
    return result;
  }
  onValueChange(value: any) {
     const quill: Quill = this.editor?.quill;
     const plainText = quill.getText();
     const charCount = plainText.length - 1; // Remove trailing newline
     const currentRange = quill.getSelection(true);
       if (this.charLimit) {
          const maxAllowedForThisField = this.charLimit - this.otherSectionsCount;
          if(charCount > maxAllowedForThisField){
             const cutoffIndex = Math.max(0, maxAllowedForThisField);
              // Only delete if we're over the allowed amount for this field
              if (cutoffIndex < charCount) {
                quill.deleteText(cutoffIndex, charCount - cutoffIndex);
                
                // Restore cursor position safely
                setTimeout(() => {
                  const newPos = Math.min(currentRange?.index || cutoffIndex, cutoffIndex);
                  try {
                    quill.setSelection(newPos, 0);
                  } catch (err) {}
                }, 0);
              }
            // Recalculate after trim
            const newPlainText = quill.getText();
            this.charCount = newPlainText.replace(REGEX.END_NEWLINE, '').length;
            this.value = this.cleanBackgroundColorFromHtml(quill.root.innerHTML);
            this.pendingLimitAlert = true;

          }
          else{
            // Under limit - update normally
            this.value = this.cleanBackgroundColorFromHtml(quill.root.innerHTML);
            this.charCount = charCount;
            this.pendingLimitAlert = false;
          }
           // Always emit the current character count and value after any change
          this.valueChange.emit({ value: this.value, charCount: this.charCount });
       }
      else{
        this.value = this.cleanBackgroundColorFromHtml(value);
        this.charCount = charCount;
        this.valueChange.emit({ value: this.value, charCount: this.charCount });
      }
      this.onChange(this.value);
      this.onTouched();
   
  }

  writeValue(value: any): void {
    this.value = this.cleanBackgroundColorFromHtml(value || '');
    setTimeout(() => {
      const quill: Quill = this.editor?.quill;

      // Emit character count after setting value
      const plainText = quill?.getText();
      this.charCount = plainText.length - 1;
      this.valueChange.emit({ value: this.value, charCount: this.charCount });
    },100);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  clearEditor() {
    const quill = this.editor.getQuill();
    quill.setSelection(0, quill.getLength()); // select all
    quill.deleteText(0, quill.getLength());   // delete all
  }

  private cleanBackgroundColorFromHtml(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const treeWalker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_ELEMENT,
      null
    );

    let node = treeWalker.nextNode(); // start with first element node

    while (node) {
      if (node instanceof HTMLElement) {
        const style = node.getAttribute('style');
        if (style) {
          // Remove only background-color from style attribute
          const newStyle = style
            .replace(REGEX.REMOVE_BG_COLOR, '')
            .replace(REGEX.REMOVE_COLOR, '')
            .trim();

          if (newStyle) {
            node.setAttribute('style', newStyle);
          } else {
            node.removeAttribute('style');
          }
        }
      }
      node = treeWalker.nextNode();
    }

    // Remove empty tags like <h2><br></h2>, <p></p>, etc.
    if (this.getTextOnly(tempDiv.innerHTML) === '') {
      this.removeEmptyNodes(tempDiv);
    }

    return tempDiv.innerHTML.trim();
  }

  /** Removes empty elements (including cases like <tag><br></tag>) */
  private removeEmptyNodes(root: HTMLElement) {
    const elements = root.querySelectorAll('*');

    elements.forEach(el => {
      const html = el.innerHTML.replace(REGEX.BR_TAG, '').trim();
      // remove if empty
      if (!html) {
        el.remove();
      }
    });
  }

  private getTextOnly(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    return tempDiv.textContent?.trim() ?? "";
  }
}